import { captureException } from "@sentry/nextjs";
import type { UseQueryResult } from "@tanstack/react-query/src/types";
import { fetcher } from "@website/fetcher/fetcher";
import { refreshToken } from "@website/fetcher/refresh-token";
import { useFetcher } from "@website/fetcher/use-fetcher";
import {
  AuthResponse,
  B2BProfile,
  DECODED_TOKEN,
  UserInfo,
  UserInfoUpdateBody
} from "@website/hooks/auth/auth.types";
import { isTokenExpired } from "@website/hooks/token";
import { applicationStore, authStore, useSelector } from "@website/store/store";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { useWebengage } from "@website/tracking/tracking.webengage.hook";
import { addEventToTracking } from "@website/tracking/user";
import { getChannel } from "@website/utils/channel/get-channel";
import { isChannel } from "@website/utils/channel/helpers";
import { setChannel } from "@website/utils/channel/set-channel";
import { setCookie } from "@website/utils/cookie";
import { getNow } from "@website/utils/server-now";
import jwtDecode from "jwt-decode";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";

setChannel();

const baseURL = process.env.NEXT_PUBLIC_BASE_URL_PROFILE;

export const AUTH_ENDPOINTS = {
  CONVERT: `${baseURL}/auth/jek`,
  REFRESH: `${baseURL}/auth/refresh-token`,
  ME: `${baseURL}/user/me`,
  B2B_PROFILE: `${baseURL}/b2b/profile`,
  UPDATE_ME: `${baseURL}/user/me`,
  LOGIN: `${baseURL}/auth/password`,
  CONVERT_SHAB: `${baseURL}/auth/shab`
};

const SHAB_REDIRECT_MODAL_KEY = "SHAB_REDIRECT_MODAL";

export const logout = () => {
  authStore.accessToken.delete();
  authStore.refreshToken.delete();
  authStore.jekToken.delete();
  authStore.delete();
  localStorage.removeItem("auth_store");
  localStorage.removeItem("access-token");
  localStorage.removeItem("refresh-token");
  applicationStore.shopping.delete();
};

const getDomain = () => {
  // like in b2c.asiatech.ptp we want to set cookie on asiatech.ptp
  try {
    const parts = window.location.hostname.split(".");
    const parentDomain = parts.slice(-2).join(".");
    return parentDomain;
  } catch (error) {
    captureException(error);
    return "snapptrip.com";
  }
};
export const saveLogin = (data: Partial<AuthResponse>) => {
  const parentDomain = getDomain();

  if (data?.accessToken) {
    authStore.accessToken.set(data?.accessToken);
    localStorage.setItem("access-token", data?.accessToken);
    setCookie("access-token", data?.accessToken, {
      path: "/"
    });
    setCookie("access-token-legacy", data?.accessToken, {
      path: "/",
      domain: parentDomain
    });
  }
  if (data?.refreshToken) {
    authStore.refreshToken.set(data?.refreshToken);
    localStorage.setItem("refresh-token", data?.refreshToken);
    setCookie("refresh-token", data?.refreshToken, { path: "/" });
    setCookie("refresh-token-legacy", data?.refreshToken, {
      path: "/",
      domain: parentDomain
    });
  }
};
export const convert = (jekToken: string) => {
  const isShab = getChannel() === "shab";
  return fetcher<AuthResponse>({
    url: isShab ? AUTH_ENDPOINTS.CONVERT_SHAB : AUTH_ENDPOINTS.CONVERT,
    method: "POST",
    data: isShab ? { shabToken: jekToken } : { snappToken: jekToken }
  }).then(({ data }) => {
    saveLogin(data);
    addEventToTracking(USER_TRACKING_EVENTS.JEK_LOGIN, {
      product: "core"
    });
  });
};

export const useJwtToken = (): DECODED_TOKEN => {
  const token = useSelector(authStore.accessToken);
  const fallback: DECODED_TOKEN = useMemo(
    () => ({
      analyticsId: "",
      email: "",
      exp: 0,
      iat: 0,
      name: "",
      phoneNumber: "",
      sub: "",
      userPushId: "",
      walletUsername: "",
      customerKey: "",
      profileType: "B2C"
    }),
    []
  );
  return useMemo(() => {
    try {
      return jwtDecode(token) || fallback;
    } catch {
      return fallback;
    }
  }, [fallback, token]);
};

export const updateUserInfo = (data: UserInfoUpdateBody) => {
  const { profileType } = useJwtToken();
  const url =
    profileType === "B2B" ? AUTH_ENDPOINTS.B2B_PROFILE : AUTH_ENDPOINTS.ME;
  return fetcher<UserInfo>({
    url,
    method: "PATCH",
    data
  }).then(({ data }) => data);
};

export const useUserInfo = () => {
  const accessToken = useSelector(authStore.accessToken);
  const { profileType } = useJwtToken();
  const url =
    profileType === "B2B" ? AUTH_ENDPOINTS.B2B_PROFILE : AUTH_ENDPOINTS.ME;
  return useFetcher({
    enabled: Boolean(accessToken),
    queryKey: [url],
    queryFn: () =>
      fetcher<UserInfo>({
        url
      }).then(({ data }) => data)
  }) as UseQueryResult<UserInfo>;
};

export const useB2BProfile = () => {
  const accessToken = useSelector(authStore.accessToken);
  return useFetcher({
    enabled: Boolean(accessToken),
    queryKey: [AUTH_ENDPOINTS.B2B_PROFILE],
    queryFn: () =>
      fetcher<B2BProfile>({
        url: AUTH_ENDPOINTS.B2B_PROFILE
      }).then(({ data }) => data)
  }) as UseQueryResult<B2BProfile>;
};

function getData<T>(url: string) {
  return new Promise((resolve) => {
    fetcher<T>({ url })
      .then(({ data }) => resolve(data))
      .catch(async () => {
        resolve(undefined);
      });
  });
}

export const useProfile = () => {
  const { profileType } = useJwtToken();
  const accessToken = useSelector(authStore.accessToken);

  const url =
    profileType === "B2B" ? AUTH_ENDPOINTS.B2B_PROFILE : AUTH_ENDPOINTS.ME;

  type ProfileType = B2BProfile | UserInfo;

  return useFetcher({
    enabled: Boolean(accessToken),
    queryKey: [url],
    queryFn: () => getData<ProfileType>(url)
  }) as UseQueryResult<ProfileType>;
};

export const useAuth = () => {
  const router = useRouter();
  const accessToken = useSelector(authStore.accessToken);

  useEffect(() => {
    const isPwa = toString(router?.query?.ispwa);
    const appVersion = toString(router?.query?.app_version);
    if (isPwa) {
      applicationStore.applicationData.isPwa.set(Boolean(isPwa));
    }
    if (appVersion) {
      applicationStore.applicationData.appVersion.set(appVersion);
    }
  }, [router.query]);

  useEffect(() => {
    if (
      getChannel() === "b2b" &&
      !accessToken &&
      !(router.asPath === "/b2b-login" || router.asPath === "/business")
    ) {
      window.location.href = `/b2b-login`;
    }
  }, [accessToken, router]);
};

export const refreshAccessToken = () => {
  const currentTime = getNow();
  const accessToken = authStore.accessToken.get();
  if (accessToken && isTokenExpired(accessToken, currentTime)) {
    const _refreshToken = authStore.refreshToken.get();

    if (!_refreshToken || isTokenExpired(_refreshToken, currentTime)) {
      logout();
    }

    refreshToken();
  }
};

export const useConvertToken = () => {
  const router = useRouter();
  const channel = getChannel();
  const { login } = useWebengage();
  const [convertTokenFinished, setConvertTokenFinished] = useState(false);
  //use this to make sure useEffect only runs once
  const ran = useRef(false);

  const token = toString(router.query?.token);
  const accessToken = toString(router.query?.accessToken);
  const refreshToken = toString(router.query?.refreshToken);
  const b2bToken = toString(router?.query?.b2btoken);

  const redirectShab = () => {
    router.replace({
      pathname: router.pathname,
      query: {
        ...router.query,
        [SHAB_REDIRECT_MODAL_KEY]: "true"
      }
    });
  };

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const isShabChannel = isChannel(["shab"]);
    const authStoreToken = isShabChannel
      ? authStore.shabToken.get()
      : authStore.jekToken.get();
    if (isShabChannel && !token && !authStoreToken) {
      redirectShab();
      setConvertTokenFinished(true);
      return;
    }
    if (token && token !== authStoreToken) {
      isShabChannel
        ? authStore.shabToken.set(token)
        : authStore.jekToken.set(token);
      convert(token)
        .then(() => {
          setConvertTokenFinished(true);
        })
        .catch(() => {
          setConvertTokenFinished(false);
          if (isShabChannel) {
            redirectShab();
          }
        });
    } else {
      saveLogin({
        accessToken: b2bToken || accessToken,
        refreshToken: refreshToken
      });
      login();
      Promise.resolve(setConvertTokenFinished(true));
    }
  }, [accessToken, b2bToken, channel, refreshToken, token, login]);

  return convertTokenFinished;
};
